/**
 * Auth Features 설정
 * 각 페이지별 브랜드 섹션에 표시되는 features 데이터
 *
 * @description
 * 로그인, 회원가입 등 auth 페이지의 브랜드 섹션에서
 * 표시되는 특징 목록을 정의합니다.
 */
import React, { memo } from "react";

// ============================================================
// 타입 정의
// ============================================================

/** Feature 항목 인터페이스 **/
export interface Feature {
    icon: React.ReactNode;
    title: string;
    description: string;
}

// ============================================================
// SVG 아이콘 컴포넌트 (메모이제이션 적용)
// ============================================================

/** 공통 SVG props */
interface SvgIconProps {
    size?: number;
}

const DEFAULT_ICON_SIZE = 24;

/** 체크 서클 아이콘 */
const CheckCircleIcon = memo<SvgIconProps>(({ size = DEFAULT_ICON_SIZE }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor">
        <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
        />
    </svg>
));
CheckCircleIcon.displayName = 'CheckCircleIcon';

/** 번개 아이콘 */
const LightningIcon = memo<SvgIconProps>(({ size = DEFAULT_ICON_SIZE }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor">
        <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M13 10V3L4 14h7v7l9-11h-7z"
        />
    </svg>
));
LightningIcon.displayName = 'LightningIcon';

/** 차트 바 아이콘 */
const ChartBarIcon = memo<SvgIconProps>(({ size = DEFAULT_ICON_SIZE }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor">
        <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
        />
    </svg>
));
ChartBarIcon.displayName = 'ChartBarIcon';

/** 잠금 아이콘 */
const LockIcon = memo<SvgIconProps>(({ size = DEFAULT_ICON_SIZE }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor">
        <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
        />
    </svg>
));
LockIcon.displayName = 'LockIcon';

/** 메일 아이콘 */
const MailIcon = memo<SvgIconProps>(({ size = DEFAULT_ICON_SIZE }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor">
        <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
        />
    </svg>
));
MailIcon.displayName = 'MailIcon';

// ============================================================
// Feature 데이터 정의
// ============================================================

/** 로그인 페이지 features */
export const LOGIN_FEATURES: Feature[] = [
    {
        icon: <CheckCircleIcon />,
        title: '실시간 정산',
        description: '정확하고 빠른 실시간 정산 시스템',
    },
    {
        icon: <LightningIcon />,
        title: '빠른 처리',
        description: '간편하고 신속한 업무 처리',
    },
    {
        icon: <ChartBarIcon />,
        title: '통계 분석',
        description: '체계적인 데이터 관리 및 분석',
    },
];

/** 본사 관리자 회원가입 features */
export const HEADQUARTERS_FEATURES: Feature[] = [
    {
        icon: <CheckCircleIcon />,
        title: '전체 협력사 관리',
        description: '모든 협력사를 통합 관리',
    },
    {
        icon: <LightningIcon />,
        title: '통합 정산 처리',
        description: '전체 라이더 정산 일괄 처리',
    },
    {
        icon: <ChartBarIcon />,
        title: '통계 및 리포트',
        description: '체계적인 데이터 분석',
    },
];

/** 협력사 회원가입 features */
export const SUPPLIER_FEATURES: Feature[] = [
    {
        icon: <CheckCircleIcon />,
        title: '소속 라이더 관리',
        description: '라이더 정보 및 정산 관리',
    },
    {
        icon: <LightningIcon />,
        title: '정산 요청',
        description: '빠르고 편리한 정산 처리',
    },
    {
        icon: <ChartBarIcon />,
        title: '정산 통계',
        description: '실시간 정산 현황 조회',
    },
];

/** 비밀번호 재설정 features */
export const RESET_PASSWORD_FEATURES: Feature[] = [
    {
        icon: <LockIcon />,
        title: '강력한 보안',
        description: '안전한 비밀번호로 계정 보호',
    },
    {
        icon: <MailIcon />,
        title: '즉시 적용',
        description: '재설정 후 바로 로그인 가능',
    },
    {
        icon: <CheckCircleIcon />,
        title: '간편한 절차',
        description: '새 비밀번호 입력만으로 완료',
    },
];

/** 비밀번호 찾기 features (로그인과 동일) */
export const FORGOT_PASSWORD_FEATURES: Feature[] = LOGIN_FEATURES;
export const REGISTER_SELECT_FEATURES: Feature[] = LOGIN_FEATURES;
